import React, { useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { ProgressSpinner } from 'primereact/progressspinner';

import axiosInstance from '../config/axiosConfig';
import useTokenStore from '../store/useTokenStore';
import useUserStore from "../store/useUserStore";
import useSelectedCompanyStore from "../store/useSelectedCompanyStore";

import Content from './Content';
import routes from './DashboardRoutes';
import { notificationHandler } from './Notification';

import dayjs from 'dayjs';

const Layout = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [notification, setNotification] = useState(false);
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [isWelcomeDialogVisible, setIsWelcomeDialogVisible] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(localStorage.getItem('sidebarIsCollapsed') || false);
    const [isLongLoading, setIsLongLoading] = useState(false);

    const hydrateStore = useTokenStore((state) => state.hydrateStore);
    const token = useTokenStore((state) => state.token);
    const clearToken = useTokenStore((state) => state.clearToken);

    const user = useUserStore((state) => state.user);
    const setUser = useUserStore((state) => state.setUser);
    const setCompanies = useUserStore((state) => state.setCompanies);

    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);
    const setSelectedCompany = useSelectedCompanyStore((state) => state.setSelectedCompany);

    useEffect(() => {
        const hydrate = async () => {
            await hydrateStore();
        };
        hydrate();
    }, [hydrateStore]);

    useEffect(() => {
        const updateLastActivity = async () => {
            const lastActivity = sessionStorage.getItem('lastActivity');
            const now = dayjs();

            if (lastActivity && dayjs(lastActivity).add(15, 'minute').isAfter(now)) return;

            try {
                await axiosInstance.post('/dashboard/user/me/updateActivity');
                sessionStorage.setItem('lastActivity', now.toISOString());
            } catch (error) {
                console.log("couldn't update user activity.");
            }
        };

        const timer = setTimeout(() => {
            setIsLongLoading(true);
        }, 20000);

        if (token) {
            axiosInstance.get('/dashboard/user/me').then(response => {
                const userData = response.data;
                setUser(userData);
                if (userData.companies) setCompanies(userData.companies);
                if (userData.selectedCompany) setSelectedCompany(userData.selectedCompany);

                if (!userData.welcome_message) {
                    setIsWelcomeDialogVisible(true);
                }

                updateLastActivity(); // Activity update, see "/dashboard/user/me/updateActivity" in web.php

                clearTimeout(timer);
            }).catch(error => {
                console.error('Error fetching user data:', error);

                if (error?.response?.status === 401) {
                    console.log('session expired.');
                    clearToken();
                    window.location.href = '/auth';
                };

                clearTimeout(timer);
            }).finally(() => {
                setIsLoading(false);
            });

            if (selectedCompany?.company_id) {
                axiosInstance.get(`/dashboard/companies/${selectedCompany?.company_id}`).then(response => {
                    const refresh_data = selectedCompany?.company_id !== response?.data?.company_id;
                    setSelectedCompany(response.data);

                    if (refresh_data) return window.location.href = '/dashboard/', window.location.reload();

                }).catch(error => {
                    console.error('Error fetching company data:', error);
                });
            } else {
                setIsLoading(false);
            }
        } else {
            setIsLoading(false);
        }

        return () => clearTimeout(timer);
    }, [token, setUser, setCompanies, clearToken]);

    useEffect(() => {
        if (!token) return;
        notificationHandler(user, selectedCompany, setNotification);
    }, [token, user, selectedCompany, setNotification]);

    if (isLoading) {
        return (
            <div className={'logo-loading-container'}>
                <img src="/images/svg/logo-deepsy-svg.svg" alt="DeePsy Logo" />
                {isLongLoading && (
                    <div className="loading-text">
                        <p>Cela prend plus de temps que d'habitude, essayez peut-être de rafraîchir la page ?</p>
                        <div className="mt-2">
                            <button className="button is-outlined mr-2" onClick={() => window.location.reload()}>
                                <span className="icon is-small">
                                    <i className="fa-solid fa-arrow-rotate-right"></i>
                                </span>
                                <span>Recharger</span>
                            </button>
                            <button className="button is-link is-outlined is-light" onClick={() => window.location.href = '/'}>
                                <span className="icon is-small">
                                    <i className="fa-solid fa-arrow-up-right-from-square"></i>
                                </span>
                                <span>Retour à DeePsy</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (!token && !isLoading) {
        window.location.href = '/auth';
        return null;
    }

    return (
        <Router>
            <Content
                routes={routes}
                notification={notification}
                isCollapsed={isCollapsed}
                setIsCollapsed={setIsCollapsed}
                selectedCompany={selectedCompany}
                isWelcomeDialogVisible={isWelcomeDialogVisible}
                setIsWelcomeDialogVisible={setIsWelcomeDialogVisible}
                isDialogVisible={isDialogVisible}
                setIsDialogVisible={setIsDialogVisible}
            />
        </Router>
    );
};

export default Layout;